import { DEFAULT_EFFORT, isEffort, isEffortAbove, type ChatEffort } from './chatEffort'

/**
 * The composer's two AI controls, as one persisted preference: which model, and
 * how hard it reasons.
 *
 * It was three until the "Thinking" tick box was folded into the effort list.
 * They were the same dial — `effort` is a field inside OpenRouter's `reasoning`
 * object and `none` is one of its values — so having both meant the tick box
 * could silently discard the chosen effort. "Disabled" is now just the cheapest
 * position on the one control.
 *
 * Global rather than per-conversation, matching how effort has always behaved.
 * Storing it per bot would mean deciding what an old conversation does when its
 * model is retired, which is a bigger question than this feature needs.
 *
 * The server re-validates both on every send and clamps them to what the plan
 * allows (Subscription.normalizeSelection), so nothing here is a security
 * boundary — a stale or hand-edited localStorage value cannot buy anything.
 */
export interface ChatSelection {
  /** Identifier from the server, e.g. 'openai/gpt-5.5'. null = use the server default. */
  model: string | null
  /**
   * null means "the user has never chosen", which is NOT the same as 'none'.
   *
   * The distinction has to survive, because the displayed value gets clamped and
   * the clamp must never be written back. A free plan is held at Disabled; if
   * that were persisted, upgrading to a paid plan would leave them silently stuck
   * there instead of picking up the paid default.
   */
  effort: ChatEffort | null
  /**
   * Auto-verify: after generating code, the server replays it over the user's
   * drawings and fixes mismatches on its own (up to a round limit).
   *
   * null means "never chosen", which the server reads as ON — the default. Only an
   * explicit false turns the loop off, so the key is sent only once the user has
   * touched the switch (see selectionPayload). Global like the other two controls:
   * it describes how the user wants the assistant to work, not one conversation.
   */
  agentic: boolean | null
}

/** The switch as it should render: unset is on. */
export function agenticEnabled(selection: Pick<ChatSelection, 'agentic'>): boolean {
  return selection.agentic !== false
}

/**
 * One model as the picker endpoint describes it.
 *
 * Every field past `contextLength` is a capability read from `tbl_model` and
 * originally from OpenRouter's catalogue, not a constant on either side. That is
 * the point: 128 of the catalogue's 414 models do no reasoning at all and 89
 * cannot be asked to stop, so "what can this control offer" is a question only
 * the server can answer.
 */
export interface ChatModelOption {
  identifier: string
  label: string
  contextLength: number
  /**
   * Efforts this model accepts, cheapest first, INCLUDING 'none' when it can be
   * turned off. Empty means the model cannot reason at all — which disables the
   * control rather than hiding it.
   */
  efforts: ChatEffort[]
  /** False disables the effort control entirely. */
  reasoning: boolean
  /** This model's own default, used when the plan default is not in its list. */
  defaultEffort: ChatEffort
}

/** Response shape of POST /ai/bot/models. */
export interface ChatModelsConfig {
  pickerEnabled: boolean
  defaultModel: string
  /** What this plan gets when the user has never chosen. */
  defaultEffort: ChatEffort
  /**
   * The most this plan may ask for; null means no ceiling. Anything above it is
   * shown with a lock and routes to the upgrade flow. Replaced the old
   * effortLocked + lockedEffort pair, because the rule is a comparison rather
   * than a boolean — and the boolean version locked options CHEAPER than the one
   * the plan allowed, which is an upgrade prompt that sells nothing.
   */
  maxEffort: ChatEffort | null
  models: ChatModelOption[]
}

const KEY = 'algobuilder.chatSelection'

/** Effort key the pre-picker build used. Read once, so a refresh keeps the tier. */
const LEGACY_EFFORT_KEY = 'algobuilder.chatEffort'

export const DEFAULT_SELECTION: ChatSelection = {
  model: null,
  // Unset, not Disabled — see ChatSelection.effort.
  effort: null,
  // Unset, which is on — see ChatSelection.agentic.
  agentic: null,
}

function parse(raw: string | null): Partial<ChatSelection> {
  if (!raw) return {}
  try {
    const value = JSON.parse(raw)
    return value && typeof value === 'object' ? value : {}
  } catch {
    return {}
  }
}

export const selectionStorage = {
  get(): ChatSelection {
    try {
      const saved = parse(localStorage.getItem(KEY))
      // Migration, not a fallback: someone mid-session picked an effort under the
      // old key and should not silently be reset to the default.
      const legacy = localStorage.getItem(LEGACY_EFFORT_KEY)

      return {
        model: typeof saved.model === 'string' ? saved.model : null,
        effort: isEffort(saved.effort) ? saved.effort : isEffort(legacy) ? legacy : null,
        agentic: typeof saved.agentic === 'boolean' ? saved.agentic : null,
      }
    } catch {
      return DEFAULT_SELECTION
    }
  },

  set(selection: ChatSelection): void {
    try {
      localStorage.setItem(KEY, JSON.stringify(selection))
    } catch {
      // storage unavailable — the selection just won't survive a refresh
    }
  },
}

/**
 * The model that will actually answer — which is not always the one we send.
 *
 * Two questions live here, and conflating them was a bug. "Which model answers"
 * decides the effort list, whether the control reasons at all, and what a stored
 * effort clamps against. "Which model do we assert on the wire" is a separate
 * contract: with the picker off the server pins DEFAULT_CHAT_MODEL and ignores
 * whatever we send, so naming one would be a lie (see selectionPayload).
 *
 * They used to be one field. `resolveSelection` reported `model: null` with the
 * picker off, every consumer then looked null up in `config.models`, missed, and
 * fell back to the full seven-tier effort vocabulary — offering values the pinned
 * model rejects, with no plan ceiling applied. The catalogue was in the payload
 * the whole time; nothing was reading it.
 */
export function activeModel(
  selection: ChatSelection,
  config: ChatModelsConfig | null,
): ChatModelOption | null {
  if (!config) return null
  const models = config.models ?? []
  // A stored choice only counts while the picker is on. Otherwise it is a
  // preference the server will discard, so it must not steer the controls either.
  const chosen = config.pickerEnabled
    ? models.find((m) => m.identifier === selection.model)
    : undefined
  // The trailing models[0] is unreachable unless defaultModel is missing from the
  // catalogue, i.e. the default is not selectable. The server logs that; showing
  // the first row beats showing nothing while someone fixes it.
  return chosen ?? models.find((m) => m.identifier === config.defaultModel) ?? models[0] ?? null
}

/**
 * Reconcile a stored preference with what the server currently offers.
 *
 * Everything the client remembers can go stale: a model can be deactivated, an
 * effort can stop being offered, and a plan change can lower the ceiling.
 * Resolving that here — once, against the server's own answer — is what keeps the
 * composer from showing a selection that a send would silently override.
 *
 * The result is for DISPLAY and for the payload. It is never written back to
 * storage; only a real user action does that (see BotDetail's updateSelection),
 * which is what lets a clamped value recover when the plan improves.
 *
 * @param stored - What localStorage had
 * @param config - What POST /ai/bot/models returned, or null while it is loading
 */
export function resolveSelection(
  stored: ChatSelection,
  config: ChatModelsConfig | null,
): ChatSelection {
  if (!config) return stored

  // The model that will answer — the default when the picker is off. The effort
  // below is clamped against ITS list, which is the whole reason this is resolved
  // rather than left null.
  const model = activeModel(stored, config)

  return {
    // Picker off: the server ignores any model we send, so naming one would be a
    // lie. The clamp above still knows which model it was.
    model: config.pickerEnabled ? (model?.identifier ?? null) : null,
    // Unset stays unset. The payload omits the key so the server applies its own
    // default, and writing a value here would both assert a preference nobody
    // expressed and bake a plan's clamp into every later request. What to SHOW
    // for an unset preference is effortValue()'s job, not this one's.
    effort: stored.effort === null ? null : resolveEffort(stored.effort, model, config),
    // Nothing on the server side to reconcile against: the switch is the user's alone.
    agentic: stored.agentic,
  }
}

/**
 * Clamp an effort to what this model offers and this plan allows.
 *
 * Mirrors Subscription.resolveEffort. The server is still the authority — this
 * exists so the composer never displays a value the send would quietly change.
 */
function resolveEffort(
  wanted: ChatEffort,
  model: ChatModelOption | null,
  config: ChatModelsConfig,
): ChatEffort {
  // Nothing known yet, or a model that cannot reason: leave it alone rather than
  // clamping against a list we do not have. The control renders disabled and the
  // server sends no reasoning field at all.
  if (!model || !model.reasoning || model.efforts.length === 0) {
    return wanted
  }

  const allowed = (candidate: ChatEffort | null): candidate is ChatEffort =>
    !!candidate && model.efforts.includes(candidate) && !isEffortAbove(candidate, config.maxEffort)

  // Plan default ahead of the model's, matching Subscription.resolveEffort: one
  // flat default is explainable, and a default that shifted as you switched
  // models would look like the control changing itself.
  const pick = [wanted, config.defaultEffort, model.defaultEffort].find(allowed)
  if (pick) return pick

  // Nothing at or below the ceiling is on offer: every effort this model accepts
  // costs more than the plan allows. It reasons regardless, so show the cheapest it
  // will accept rather than a tier it cannot honour. Mirrors the last line of
  // Subscription.resolveEffort.
  return model.efforts[0]
}

/** Is the effort control meaningful for this selection? False disables it. */
export function hasReasoning(
  selection: ChatSelection,
  config: ChatModelsConfig | null,
): boolean {
  if (!config) return true
  // The model that will answer, not the one on the wire — with the picker off
  // those differ, and it is the pinned model's capability that decides whether
  // this control means anything.
  const model = activeModel(selection, config)
  // Still nothing known (config loading, or an empty catalogue): assume yes rather
  // than disabling a control the server may well honour. The send is re-validated
  // either way.
  return model ? model.reasoning : true
}

/**
 * The effort to render as selected, which unlike the stored preference cannot be
 * null — the control always shows something.
 *
 * Substitutes the plan default for an unset preference and then clamps exactly as
 * a send would, so what the user sees is what the server will do. The clamp
 * matters for more than tidiness: on a plan capped at Disabled, a model that
 * cannot stop reasoning resolves to its cheapest tier, and showing "Disabled"
 * there would be a control claiming something it cannot deliver.
 */
export function effortValue(
  selection: ChatSelection,
  config: ChatModelsConfig | null,
): ChatEffort {
  if (!config) return selection.effort ?? DEFAULT_EFFORT
  const model = activeModel(selection, config)
  return resolveEffort(selection.effort ?? config.defaultEffort, model, config)
}

/**
 * The selection as a send payload. Each key is omitted when unset so the server
 * applies its own default rather than being handed a null to interpret.
 *
 * `effort` in particular: sending the displayed value would assert a preference
 * nobody expressed, and would bake a plan's clamp into every future request.
 */
export function selectionPayload(selection: ChatSelection) {
  return {
    ...(selection.model ? { model: selection.model } : {}),
    ...(selection.effort ? { effort: selection.effort } : {}),
    // Both true and false are real choices once made; only "never touched" is omitted.
    ...(typeof selection.agentic === 'boolean' ? { agentic: selection.agentic } : {}),
  }
}
